import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { Address, parseAbiItem } from "viem";

interface Coin {
  id: string;
  coinAddress?: string;
  transactionHash?: string;
  name: string;
  ticker: string;
  marketCap: string;
  volume: string;
  change24h: string;
  holders: number;
  timeAgo: string;
}

const TRANSFER_EVENT = parseAbiItem("event Transfer(address indexed from, address indexed to, uint256 value)");
const BALANCE_OF_ABI = [parseAbiItem("function balanceOf(address account) view returns (uint256)")];
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

const HolderCountCell = ({ coin }: { coin: Coin }) => {
  const publicClient = usePublicClient({ chainId: 137 });
  const { data, isLoading } = useQuery({
    queryKey: ["holder-count", coin.coinAddress, coin.transactionHash],
    enabled: Boolean(publicClient && coin.coinAddress && /^0x[a-fA-F0-9]{40}$/.test(coin.coinAddress)),
    staleTime: 60_000,
    queryFn: async () => {
      if (!publicClient || !coin.coinAddress) return coin.holders;
      try {
        const latest = await publicClient.getBlockNumber();
        let fromBlock = latest > 100000n ? latest - 100000n : 0n;
        if (coin.transactionHash && /^0x[a-fA-F0-9]{64}$/.test(coin.transactionHash)) {
          const receipt = await publicClient.getTransactionReceipt({ hash: coin.transactionHash as `0x${string}` });
          fromBlock = receipt.blockNumber;
        }
        const addresses = new Set<string>();
        const chunkSize = 10_000n;
        for (let start = fromBlock; start <= latest; start += chunkSize) {
          const end = start + chunkSize - 1n > latest ? latest : start + chunkSize - 1n;
          const logs = await publicClient.getLogs({ address: coin.coinAddress as Address, event: TRANSFER_EVENT, fromBlock: start, toBlock: end });
          logs.forEach((log) => {
            const from = log.args.from?.toLowerCase();
            const to = log.args.to?.toLowerCase();
            if (from && from !== ZERO_ADDRESS) addresses.add(from);
            if (to && to !== ZERO_ADDRESS) addresses.add(to);
          });
        }
        if (!addresses.size) return coin.holders;
        const balances = await Promise.all([...addresses].map(async (holder) => {
          try {
            return (await publicClient.readContract({ address: coin.coinAddress as Address, abi: BALANCE_OF_ABI, functionName: "balanceOf", args: [holder as Address] })) as bigint;
          } catch { return 0n; }
        }));
        return balances.filter((balance) => balance > 0n).length;
      } catch {
        return coin.holders;
      }
    },
  });
  return <TableCell>{isLoading ? "—" : data ?? coin.holders}</TableCell>;
};

interface CoinsTableProps {
  coins: Coin[];
}

const CoinsTable = ({ coins }: CoinsTableProps) => {
  const navigate = useNavigate();

  const handleCoinClick = (coin: Coin) => {
    navigate(`/coins/${coin.id}`);
  };

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>COIN</TableHead>
            <TableHead>CHART</TableHead>
            <TableHead>HOLDERS</TableHead>
            <TableHead>THREADS</TableHead>
            <TableHead>QUICK BUY</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {coins.map((coin) => (
            <TableRow 
              key={coin.ticker} 
              className="hover:bg-muted/50 cursor-pointer"
              onClick={() => handleCoinClick(coin)}
            >
              <TableCell>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{coin.name}</span>
                    <span className="text-xs text-muted-foreground">{coin.ticker}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <span>MC {coin.marketCap}</span>
                    <span className="mx-2">•</span>
                    <span>Vol {coin.volume}</span>
                    <span className="mx-2">•</span>
                    <Clock className="h-3 w-3 mr-1" />
                    <span>{coin.timeAgo}</span>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="w-32 h-12 rounded bg-muted/20 flex items-center justify-center text-[10px] text-muted-foreground">
                  {coin.volume === "$—" ? "No price history" : "View chart"}
                </div>
              </TableCell>
              <HolderCountCell coin={coin} />
              <TableCell>0</TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="sm">0.01</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CoinsTable;
